import React, { useCallback, useEffect, useLayoutEffect, useMemo, useState, } from 'react';
import Animated, { Easing, useAnimatedStyle, useSharedValue, withTiming, } from 'react-native-reanimated';
import { AppView, ButtonGroup, CardView, rem, SCROLL_INDICATOR_INSETS, SettingsItem, SettingsSection, TextButton, withAnimatedNavigationHeader, } from 'design-system';
import Button, { HEIGHT_PLUS_PADDING } from 'design-system/Button';
import StatusBar from 'design-system/StatusBar';
import useStyles from 'hooks/useStyles';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { useMarginBottom } from 'hooks/useMarginBottom';
import createStyleSheets from 'utils/createStyleSheets';
import { Amplitude } from 'utils';
import Links from 'utils/links';
import ScreenInfoHeader from 'design-system/ScreenInfoHeader';
import HelpScreenButton from 'features/help/components/HelpScreenButton';
import useRequiredBenefitCallback from 'features/premium/hooks/useRequiredBenefitCallback';
import { IconDocument } from 'design-system/icons';
import Text from 'design-system/Text';
import { getUserAddresses } from 'actions';
import { getTradingAccounts } from 'features/invest/actions';
import { getPotsAccount } from 'features/pots/actions';
import { selectIsFetchingUserAddresses } from 'reducers/selectors';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { init as initDeviceRisk } from 'utils/packages/deviceRisk';
import { View } from 'react-native';
import useCreditScoreNavigation from '../hooks/useCreditScoreNavigation';
import useCreditScoreRegistration from '../hooks/useCreditScoreRegistration';
import { clearCreditScoreOnboarding } from '../reducers/creditScoreOnboarding';
import CreditScoreChart from '../components/CreditScoreChart';
import { selectIsCreditScoreRegistered } from '../selectors';
const strings = {
    headerTitle: 'Credit score',
    title: 'Check and improve your credit score',
    subTitle: 'Get a full picture of your financial health and see how to unlock better rates',
    button: 'Continue',
    termsLink: 'T&Cs',
    termsSuffix: ' apply',
};
const CARD_PADDING = rem(40);
const PADDING = rem(16);
const SLIDE_DISTANCE = rem(60);
const ANIMATION_DURATION = 1000;
const TERMS_ROW_HEIGHT = rem(48);
const FLOATING_CTA_HEIGHT = HEIGHT_PLUS_PADDING + TERMS_ROW_HEIGHT;
const CreditScoreOnboardingScreen = withAnimatedNavigationHeader(({ scrollHandler, navigation }) => {
    const styles = useStyles(styleSet);
    const { width, paddingHorizontalStyle } = useAppFrameDimensions();
    const scrollBottomPadding = useMarginBottom('paddingBottom', FLOATING_CTA_HEIGHT);
    const dispatch = useAppDispatch();
    const { navigateToNextStep } = useCreditScoreNavigation();
    const isRegistered = useAppSelector(selectIsCreditScoreRegistered);
    const { canRegisterAgain } = useCreditScoreRegistration();
    const isFetchingUserAddresses = useAppSelector(selectIsFetchingUserAddresses);
    const [isRefreshingKycState, setIsRefreshingKycState] = useState(false);
    useEffect(() => {
        Amplitude.logEvent('Credit Score Onboarding Intro Screen');
        return () => {
            dispatch(clearCreditScoreOnboarding());
        };
    }, []);
    useLayoutEffect(() => {
        navigation.setOptions({
            title: strings.headerTitle,
            headerRight: () => <HelpScreenButton />,
        });
    }, []);
    const onPressTerms = useCallback(() => {
        Links.openKnownLink('creditScoreTerms');
    }, []);
    const onPressContinue = useCallback(async () => {
        if (!isRegistered) {
            initDeviceRisk();
            // Refresh everything the KYC gate reads before deciding which modal
            // to show: the address status and the user-level verification on the
            // pots/trading accounts (KYC flips server-side within minutes).
            setIsRefreshingKycState(true);
            try {
                await Promise.all([
                    dispatch(getUserAddresses()),
                    dispatch(getPotsAccount()),
                    dispatch(getTradingAccounts()),
                ]);
            }
            finally {
                setIsRefreshingKycState(false);
            }
            canRegisterAgain(navigateToNextStep);
            return;
        }
        navigation.goBack();
    }, [canRegisterAgain, isRegistered, navigateToNextStep]);
    const guardedPressContinue = useRequiredBenefitCallback(onPressContinue, 'creditScore');
    const chartDiameter = useMemo(() => {
        const defaultDiameter = 250;
        if (width - (PADDING * 2 + CARD_PADDING) < defaultDiameter) {
            return 200;
        }
        return defaultDiameter;
    }, [width]);
    const cardOpacity = useSharedValue(0);
    const cardTranslateX = useSharedValue(SLIDE_DISTANCE);
    const settingsOpacity = useSharedValue(0);
    const settingsTranslateX = useSharedValue(-SLIDE_DISTANCE);
    const cardAnimatedStyle = useAnimatedStyle(() => ({
        opacity: cardOpacity.value,
        transform: [{ translateX: cardTranslateX.value }],
    }));
    const settingsAnimatedStyle = useAnimatedStyle(() => ({
        opacity: settingsOpacity.value,
        transform: [{ translateX: settingsTranslateX.value }],
    }));
    useEffect(() => {
        const config = {
            duration: ANIMATION_DURATION,
            easing: Easing.out(Easing.cubic),
        };
        cardOpacity.value = withTiming(1, config);
        cardTranslateX.value = withTiming(0, config);
        settingsOpacity.value = withTiming(1, config);
        settingsTranslateX.value = withTiming(0, config);
    }, [cardOpacity, cardTranslateX, settingsOpacity, settingsTranslateX]);
    return (<AppView withFrame={false}>
          <StatusBar barStyle="dark-content" animated/>
          <Animated.ScrollView onScroll={scrollHandler} scrollEventThrottle={16} scrollIndicatorInsets={SCROLL_INDICATOR_INSETS} contentContainerStyle={[
            paddingHorizontalStyle,
            scrollBottomPadding,
            styles.scrollContent,
        ]}>
            <ScreenInfoHeader title={strings.title} subTitle={strings.subTitle} style={styles.headerPadding}/>
            <View style={styles.cardsContainer}>
              <View style={{
            transform: [{ scale: 0.85 }],
            maxWidth: rem(450),
            width: '100%',
        }}>
                <Animated.View style={cardAnimatedStyle}>
                  <CardView noMarginTop style={{
            padding: rem(40),
            transform: [{ translateX: rem(20) }],
        }}>
                    <CreditScoreChart score={634} minScore={0} maxScore={710} animationDuration={1400} withoutTooltip noVerticalPadding diameter={chartDiameter}/>
                    <Text TextThin-14 Secondary centered>
                      Next update in 2 days
                    </Text>
                  </CardView>
                </Animated.View>
                <Animated.View style={settingsAnimatedStyle}>
                  <SettingsSection noMarginTop style={{
            transform: [{ translateX: rem(-20) }],
        }}>
                    <SettingsItem titleFirst icon={<IconDocument alt2 gradientKey="darkPurple"/>} title="Credit report" description="Credit score is calculated using information in your credit report"/>
                  </SettingsSection>
                </Animated.View>
              </View>
            </View>
          </Animated.ScrollView>
          <ButtonGroup type="floating">
            <TextButton onPress={onPressTerms} style={styles.terms}>
              <Text TextThin-14 Secondary>
                <Text TextThin-14 Primary>
                  {strings.termsLink}
                </Text>
                {strings.termsSuffix}
              </Text>
            </TextButton>
            <Button brand title={strings.button} isFetching={isFetchingUserAddresses || isRefreshingKycState} onPress={guardedPressContinue}/>
          </ButtonGroup>
        </AppView>);
}, ({ colors }) => ({
    hideTitleOnScroll: true,
    title: strings.headerTitle,
    headerColorOutputRange: [
        colors.background.dark,
        colors.cards.onDark,
    ],
}));
const styleSet = createStyleSheets(() => ({
    headerPadding: {
        marginTop: rem(8),
    },
    terms: {
        marginBottom: rem(8),
        alignItems: 'center',
    },
    scrollContent: {
        flexGrow: 1,
    },
    cardsContainer: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
    },
}));
export default CreditScoreOnboardingScreen;
